import { Sheet } from '@/ui/core/sheet'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { type LayoutChangeEvent, View, useWindowDimensions } from 'react-native'
import Animated, {
	Easing,
	ReduceMotion,
	cancelAnimation,
	runOnJS,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from 'react-native-reanimated'

/** What a stacked sheet is showing: the list, or one item, reached from the list or not. */
export type StackView<T> = { kind: 'list' } | { kind: 'item'; item: T; fromList: boolean }

const MOVE = {
	duration: 320,
	easing: Easing.bezier(0.2, 0, 0, 1),
	reduceMotion: ReduceMotion.System,
}

/**
 * One sheet that holds a list and steps into an item in place. The two sit side by side and the
 * pair slides one width, while the sheet's height eases from the one to the other, so the card
 * moves as a single piece. Opened straight on an item it shows that item alone.
 */
export const SheetStack = <T,>({
	view,
	onClose,
	list,
	item,
}: {
	view: StackView<T> | null
	onClose: () => void
	list: ReactNode
	item: (value: T) => ReactNode
}) => {
	const { width: screen, height: screenHeight } = useWindowDimensions()
	// The sheet shows at most 88% of the screen, so nothing is gained by easing the frame past it:
	// what is past it is scrolled to, not grown into.
	const shownMax = screenHeight * 0.88
	// The sheet's content width: the screen less its padding. Measured once laid out.
	const [width, setWidth] = useState(screen - 40)
	const x = useSharedValue(0)
	const height = useSharedValue<number | null>(null)

	// While moving, both sides are mounted; `to` names where we are going.
	const [moving, setMoving] = useState<'list' | 'item' | null>(null)
	const lastItem = useRef<T | null>(null)
	if (view?.kind === 'item') lastItem.current = view.item
	const previous = useRef<StackView<T> | null>(null)
	// Each side's last measured height, so a move knows where it starts from.
	const heights = useRef({ list: 0, item: 0 })
	// Before paint, so the frame is pinned and the pair offset before either side shows.
	useLayoutEffect(() => {
		const from = previous.current
		previous.current = view
		if (from?.kind === 'list' && view?.kind === 'item') {
			height.value = heights.current.list
			x.value = 0
			setMoving('item')
		} else if (from?.kind === 'item' && view?.kind === 'list') {
			height.value = heights.current.item
			// The list mounts on the left, so start the pair one width over.
			x.value = -width
			setMoving('list')
		}
	}, [view, width, x, height])
	// The leaving side is gone from the tree now; only then may the offset go back to zero and the
	// frame come to rest on the side it landed on.
	//
	// It comes to rest on that side's own height, and is never handed back its natural one. The
	// frame's height is animated, and an animated style that stops naming a property does not put
	// that property back — nor does taking the whole style off. Released, the frame kept whichever
	// height the move had last written and went on bottom-aligning its content, so a record taller
	// than the sheet showed as a sliver of its own last few answers with nothing to scroll. Held at
	// the side's true height, however far past the sheet that goes, the sheet has something to
	// scroll and the content sits where it was put.
	useLayoutEffect(() => {
		if (moving !== null) return
		cancelAnimation(x)
		cancelAnimation(height)
		x.value = 0
		const side = view?.kind === 'item' ? 'item' : 'list'
		if (heights.current[side] > 0) height.value = heights.current[side]
	}, [moving, view, x, height])

	// Which side has already begun its arrival, so a second layout pass does not restart it.
	const arrived = useRef<'list' | 'item' | null>(null)
	if (moving === null) arrived.current = null
	const settle = () => setMoving(null)
	const measure = (side: 'list' | 'item') => (event: LayoutChangeEvent) => {
		heights.current[side] = event.nativeEvent.layout.height
		// Standing still, the frame is simply this side's height, and follows it: a record is
		// fetched on the way in, so the panel the sheet lands on grows once the answers arrive.
		if (moving === null) {
			height.value = heights.current[side]
			return
		}
		// The destination has laid out: ease the height to it and slide, then drop the scaffolding.
		if (moving !== side || arrived.current === side) return
		arrived.current = side
		height.value = withTiming(heights.current[side], MOVE)
		x.value = withTiming(side === 'item' ? -width : 0, MOVE, (done) => {
			if (done) runOnJS(settle)()
		})
	}
	const rowStyle = useAnimatedStyle(() => ({ transform: [{ translateX: x.value }] }))
	const frameStyle = useAnimatedStyle(() =>
		height.value === null
			? {}
			: { height: height.value, overflow: 'hidden', justifyContent: 'flex-end' }
	)
	// Both sides stand on the sheet's foot while the pair moves, so a side taller than the sheet
	// would put its foot on the frame's and slide across its last lines. Held to what the sheet can
	// show, its head is what travels, and what it comes to rest on is what was already on screen.
	const sideCap =
		moving === null ? null : ({ maxHeight: shownMax, overflow: 'hidden' } as const)

	const showList =
		view?.kind === 'list' || moving === 'list' || (moving === 'item' && !!view)
	const showItem = view?.kind === 'item' || moving === 'list'
	const itemValue = view?.kind === 'item' ? view.item : lastItem.current

	return (
		<Sheet open={!!view} onClose={onClose}>
			<Animated.View
				style={frameStyle}
				onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
			>
				{/* Both sides stand on the sheet's foot, so a height change grows or shrinks from the top. */}
				<Animated.View
					style={[
						{
							flexDirection: 'row',
							alignItems: 'flex-end',
							width: moving ? width * 2 : width,
						},
						rowStyle,
					]}
				>
					{showList && (
						<View style={[{ width }, sideCap]} onLayout={measure('list')}>
							{list}
						</View>
					)}
					{showItem && itemValue !== null && (
						<View style={[{ width }, sideCap]} onLayout={measure('item')}>
							{item(itemValue)}
						</View>
					)}
				</Animated.View>
			</Animated.View>
		</Sheet>
	)
}
